import { create } from 'zustand';
import { api, getAuthToken, setAuthToken } from './api';
import type { AuthUser, ShareSnapshot } from './domain';

type AuthState = {
  status: 'loading' | 'anon' | 'authed';
  user: AuthUser | null;
  busy: boolean;
  error: string | null;
  registrationOpen: boolean;

  /** 只读共享演示 */
  shareToken: string | null;
  shareSnapshot: ShareSnapshot | null;
  shareLoading: boolean;
  shareError: string | null;

  bootstrap: () => Promise<void>;
  /** 重新拉取当前用户（改密/改名后同步）。 */
  refresh: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  loadShare: (token: string) => Promise<void>;
  exitShare: () => void;
  clearError: () => void;
};

const SHARE_PARAM = 'share';

function shareTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const token = new URLSearchParams(window.location.search).get(SHARE_PARAM);
  return token && token.length >= 8 ? token : null;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  busy: false,
  error: null,
  registrationOpen: true,

  shareToken: shareTokenFromUrl(),
  shareSnapshot: null,
  shareLoading: false,
  shareError: null,

  async bootstrap() {
    const shareToken = get().shareToken;
    if (shareToken) {
      await get().loadShare(shareToken);
    }
    if (!getAuthToken()) {
      set({ status: 'anon', user: null });
      return;
    }
    try {
      const { user } = await api.me();
      set({ status: 'authed', user });
    } catch {
      setAuthToken(null);
      set({ status: 'anon', user: null });
    }
  },

  async refresh() {
    try {
      const { user } = await api.me();
      set({ status: 'authed', user });
    } catch {
      /* 令牌无效时由 401 事件统一处理 */
    }
  },

  async login(identifier, password) {
    set({ busy: true, error: null });
    try {
      const { token, user } = await api.login({ identifier, password });
      setAuthToken(token);
      set({ status: 'authed', user, busy: false });
      if (get().shareToken) get().exitShare();
      return true;
    } catch (error) {
      set({ busy: false, error: (error as Error).message });
      return false;
    }
  },


  async logout() {
    try {
      await api.logout();
    } catch {
      /* 忽略网络异常 */
    }
    setAuthToken(null);
    set({ status: 'anon', user: null });
  },

  async loadShare(token) {
    set({ shareLoading: true, shareError: null, shareToken: token });
    try {
      const snapshot = await api.shareSnapshot(token);
      set({ shareSnapshot: snapshot, shareLoading: false });

      // 把快照注入主 store，直接复用只读画布与面板
      const { useStore } = await import('./store');
      useStore.setState({
        engagements: [
          {
            id: snapshot.engagement.id,
            ownerId: null,
            myRole: 'viewer',
            caseNo: snapshot.engagement.caseNo,
            name: snapshot.engagement.name,
            client: null,
            purpose: snapshot.engagement.purpose,
            legalBasis: null,
            scope: snapshot.engagement.scope ?? {},
            classification: snapshot.engagement.classification,
            priority: snapshot.engagement.priority,
            lead: snapshot.engagement.lead,
            authorizedBy: null,
            authorizedAt: null,
            expiresAt: snapshot.engagement.expiresAt,
            status: 'active',
            createdAt: '',
            updatedAt: '',
          },
        ],
        activeEngagementId: snapshot.engagement.id,
        targets: snapshot.targets,
        entities: snapshot.entities,
        relations: snapshot.relations,
        evidence: snapshot.evidence,
        hypotheses: snapshot.hypotheses,
        timeline: snapshot.timeline,
        stats: {
          targets: snapshot.stats.targets,
          entities: snapshot.stats.entities,
          evidence: snapshot.stats.evidence,
          relations: snapshot.stats.relations,
          hypotheses: snapshot.stats.hypotheses,
          byType: [],
        },
        boardNodes: snapshot.board.nodes,
        boardViewport: snapshot.board.viewport,
        boardStyle: snapshot.board.style,
        boardLoadedFor: snapshot.engagement.id,
        activeTargetId: snapshot.targets[0]?.id ?? null,
        audit: [],
        booted: true,
        loading: false,
      });
      useStore.getState().setReadOnlyView(true);
    } catch (error) {
      set({ shareError: (error as Error).message, shareLoading: false, shareSnapshot: null });
    }
  },

  exitShare() {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete(SHARE_PARAM);
      window.history.replaceState({}, '', url.toString());
    }
    void import('./store').then(({ useStore }) => useStore.getState().setReadOnlyView(false));
    set({ shareToken: null, shareSnapshot: null, shareError: null });
  },

  clearError() {
    set({ error: null });
  },
}));

/** 会话失效时自动登出。 */
if (typeof window !== 'undefined') {
  window.addEventListener('expotar:unauthorized', () => {
    const state = useAuthStore.getState();
    if (state.status === 'authed') {
      setAuthToken(null);
      useAuthStore.setState({ status: 'anon', user: null, error: '登录已过期，请重新登录' });
    }
  });
}